import { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Calendar as CalIcon, Trash2, Plus, Instagram, Music2, MapPin, Facebook, Clock,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_META = {
  Instagram: { icon: Instagram, color: "from-pink-500 to-rose-500" },
  TikTok: { icon: Music2, color: "from-cyan-400 to-fuchsia-500" },
  "Google Business": { icon: MapPin, color: "from-emerald-500 to-blue-500" },
  Facebook: { icon: Facebook, color: "from-blue-600 to-indigo-500" },
};

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ title: "", platform: "Instagram", scheduled_at: "", content: "" });
  const [open, setOpen] = useState(false);

  const load = () => {
    apiClient.get("/calendar").then(r => setEvents(r.data));
    apiClient.get("/calendar/suggested-times").then(r => setSuggestions(r.data.suggestions));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/calendar", form);
      toast.success("Publication planifiée");
      setForm({ title: "", platform: "Instagram", scheduled_at: "", content: "" });
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => { await apiClient.delete(`/calendar/${id}`); load(); };

  const grouped = {};
  events.forEach(ev => {
    const d = new Date(ev.scheduled_at);
    const key = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Calendrier</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
            Votre semaine, <span className="font-serif-italic font-normal">orchestrée</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Planifiez vos publications, repérez les meilleurs créneaux et gardez une cadence régulière.
          </p>
        </div>
        <Button className="rounded-full btn-glow" onClick={() => setOpen(o => !o)} data-testid="new-event-btn">
          <Plus className="h-4 w-4 mr-1.5" />Nouvelle publication
        </Button>
      </div>

      {open && (
        <form onSubmit={create} className="border border-border rounded-2xl p-6 bg-card grid grid-cols-1 md:grid-cols-2 gap-4 fade-up" data-testid="event-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Titre</Label>
            <Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-md h-11" data-testid="event-title" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Plateforme</Label>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger className="rounded-md h-11" data-testid="event-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(PLATFORM_META).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Date & heure</Label>
            <Input type="datetime-local" required value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="rounded-md h-11" data-testid="event-date" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Contenu</Label>
            <Textarea rows={4} required value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="rounded-md" placeholder="Le contenu à publier…" data-testid="event-content" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-full btn-glow" data-testid="event-save-btn">Planifier</Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-4">
          {Object.keys(grouped).length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-14 text-center relative overflow-hidden" data-testid="no-events">
              <div className="absolute inset-0 dot-bg opacity-30" />
              <div className="relative">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
                  <CalIcon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mt-5 text-2xl font-black font-display tracking-tighter">
                  Donnez du <span className="font-serif-italic font-normal">rythme</span>
                </h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Planifiez votre première publication et installez une présence régulière.
                </p>
                <Button className="mt-6 rounded-full btn-glow" onClick={() => setOpen(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />Planifier ma première publication
                </Button>
              </div>
            </div>
          ) : (
            Object.entries(grouped).map(([day, items]) => (
              <div key={day} className="border border-border rounded-2xl bg-card overflow-hidden">
                <div className="px-5 py-3 border-b border-border bg-gradient-to-r from-muted/40 to-transparent flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold capitalize">{day}</p>
                  <span className="text-xs text-muted-foreground">{items.length} publication{items.length > 1 ? "s" : ""}</span>
                </div>
                <ul className="divide-y divide-border">
                  {items.map(ev => {
                    const meta = PLATFORM_META[ev.platform] || PLATFORM_META.Instagram;
                    const Icon = meta.icon;
                    return (
                      <li key={ev.event_id} className="p-5 flex items-start gap-4 hover:bg-accent/30 transition-colors group" data-testid={`event-${ev.event_id}`}>
                        <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center text-white shrink-0`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-sm">{ev.title}</p>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-bold uppercase tracking-wider">{ev.platform}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />{new Date(ev.scheduled_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </p>
                          <p className="text-sm mt-2 text-foreground/80 line-clamp-2 leading-relaxed">{ev.content}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => remove(ev.event_id)} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-event-${ev.event_id}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="border border-border rounded-2xl p-5 bg-gradient-to-br from-primary/5 to-violet-500/5 h-fit">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-1">Créneaux conseillés</p>
          <p className="text-sm text-muted-foreground mb-4">Les meilleurs moments pour engager votre audience locale.</p>
          <ul className="space-y-2">
            {suggestions.map((s, i) => {
              const meta = PLATFORM_META[s.platform] || PLATFORM_META.Instagram;
              const Icon = meta.icon;
              return (
                <li key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-card border border-border hover:border-primary/40 transition-colors">
                  <div className={`h-7 w-7 rounded-md bg-gradient-to-br ${meta.color} flex items-center justify-center text-white shrink-0`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold capitalize">{s.day}</p>
                    <p className="text-xs text-muted-foreground">{s.platform}</p>
                  </div>
                  <span className="text-xs font-mono text-primary font-bold tabular-nums">{s.time}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
